

import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Card,
  Tabs,
  Tab,
  Box,
  IconButton,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Switch
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Add as AddIcon, Delete as DeleteIcon, Help as HelpIcon } from '@mui/icons-material';
import { useAuth } from 'auth/Auth';
import { useTravelUi } from 'hooks/useTravelUi';
import { CircularProgress } from '@mui/material';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customfield-tabpanel-${index}`}
      aria-labelledby={`customfield-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const FIELD_TYPES = [
  { value: 'string', label: 'Text' },
  { value: 'int', label: 'Number' },
  { value: 'boolean', label: 'Boolean' }
];

const CRM_OBJECTS = [
  { value: 'leads', label: 'Leads' },
  { value: 'contacts', label: 'Contacts' },
  { value: 'deals', label: 'Deals' },
  { value: 'accounts', label: 'Accounts' },
  { value: 'orders', label: 'Orders', legacyCrm: true },
  { value: 'quotations', label: 'Quotations', legacyCrm: true },
  { value: 'products', label: 'Products', legacyCrm: true },
];

function CustomFieldsSettings() {
  const { axiosInstance } = useAuth();
  const { showTravel } = useTravelUi();
  const visibleCrmObjects = useMemo(
    () => CRM_OBJECTS.filter((obj) => !(showTravel && obj.legacyCrm)),
    [showTravel],
  );
  // Object key (not tab index) so filtering legacy CRM objects doesn't shift selection.
  const [selectedObject, setSelectedObject] = useState('leads');
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    fieldId: null,
    tableName: null
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  // Add with other state declarations at the top of the component
  const [isDeleting, setIsDeleting] = useState(false);
  // Add with other state declarations at the top of the component
  const [isAddingField, setIsAddingField] = useState(false);
  const [newField, setNewField] = useState({
    table_name: '',
    ui_label: '',
    type: 'string',
    is_required: false
  });

  const selectedCrmObject =
    visibleCrmObjects.find((obj) => obj.value === selectedObject) || visibleCrmObjects[0];

  useEffect(() => {
    if (
      selectedCrmObject &&
      selectedObject !== selectedCrmObject.value
    ) {
      setSelectedObject(selectedCrmObject.value);
    }
  }, [visibleCrmObjects, selectedObject, selectedCrmObject]);

  const fetchCustomFields = async () => {
    if (!selectedCrmObject) return;
    try {
      const response = await axiosInstance.get(`/custom-fields/${selectedCrmObject.value}`);
      setCustomFields(Array.isArray(response.data) ? response.data : []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      // setSnackbar({
      //   open: true,
      //   message: error.response?.data?.detail,
      //   severity: 'error'
      // });
      setLoading(false);
      setCustomFields([]);
    }
  };

  useEffect(() => {
    fetchCustomFields();
  }, [selectedObject, selectedCrmObject?.value]);

  const validateField = (field, value) => {
    const errors = {};

    if (field === 'ui_label') {
      if (!value.trim()) {
        errors.ui_label = 'Field name is required';
      } else if (value.length > 50) {
        errors.ui_label = 'Field name must be less than 50 characters';
      }

      // Check for uniqueness in current table
      const currentTable = selectedCrmObject?.value;
      const existingFields = customFields.filter(f => f.table_name === currentTable);
      if (existingFields.some(f => f.ui_label.toLowerCase() === value.toLowerCase())) {
        errors.ui_label = 'Field name must be unique';
      }
    }

    return errors;
  };

  const handleFieldChange = (fieldId, name, value) => {
    // Track which fields have been edited
    setEditedFields(prev => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        [name]: value
      }
    }));

    setCustomFields(prev => prev.map(field => {
      if (field.id === fieldId) {
        return {
          ...field,
          [name]: value
        };
      }
      return field;
    }));
  };


  const handleAddField = async () => {
    setIsAddingField(true); // Start loading
    try {
      const payload = {
        ui_label: newField.ui_label,
        type: newField.type,
        is_required: Boolean(newField.is_required),
        table_name: selectedCrmObject?.value
      };

      const response = await axiosInstance.post('/custom-fields', payload);

      // Refresh the list after adding
      fetchCustomFields();

      // Reset form
      setNewField({
        ui_label: '',
        type: 'string',
        is_required: false
      });

      setSnackbar({
        open: true,
        message: 'Custom field added successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding custom field:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Error adding custom field',
        severity: 'error'
      });
    } finally {
      setIsAddingField(false); // Stop loading
    }
  };

  const handleUpdateField = async (field) => {
    try {
      const payload = {
        ui_label: field.ui_label,
        type: field.type.toLowerCase(), // Ensure type is lowercase
        is_required: field.is_required,
        table_name: selectedCrmObject?.value
      };

      await axiosInstance.put(`/custom-fields/${field.id}`, payload);
      console.log('##UpdatePayload' + payload);


      // Clear the edited state for this field
      setEditedFields(prev => {
        const newState = { ...prev };
        delete newState[field.id];
        return newState;
      });

      // Refresh the list after updating
      fetchCustomFields();

      setSnackbar({
        open: true,
        message: 'Custom field updated successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating custom field:', error);

      // Extract error message from response
      let errorMessage = 'Error updating custom field';

      // Handle string error messages directly
      if (typeof error.response?.data === 'string') {
        errorMessage = error.response.data;
      }
      // Handle error object with detail property
      else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  // Update the handleDeleteField function
  const handleDeleteField = async (fieldId) => {
    setIsDeleting(true); // Start loading
    try {
      await axiosInstance.delete(`/custom-fields/${fieldId}`);

      // Close the delete dialog
      closeDeleteDialog();

      // Refresh the list after deleting
      fetchCustomFields();

      setSnackbar({
        open: true,
        message: 'Custom field deleted successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting custom field:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Error deleting custom field',
        severity: 'error',
      });
    } finally {
      setIsDeleting(false); // Stop loading
    }
  };

  const openDeleteDialog = (tableName, fieldId) => {
    setDeleteDialog({
      open: true,
      fieldId,
      tableName
    });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      fieldId: null,
      tableName: null
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Helper function to check if a field has been edited
  const isFieldEdited = (fieldId) => {
    return !!editedFields[fieldId];
  };

  const renderFields = () => (
    <Grid container spacing={3}>
      {customFields.map((field) => (
        <Grid item xs={12} key={field.id}>
          <Card>
            <MDBox p={3}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Field Label"
                    value={field.ui_label}
                    onChange={(e) => handleFieldChange(field.id, 'ui_label', e.target.value)}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '40px'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Data Type</InputLabel>
                    <Select
                      value={field.type}
                      label="Data Type"
                      onChange={(e) => handleFieldChange(field.id, 'type', e.target.value)}
                      sx={{
                        height: '40px',
                        '& .MuiOutlinedInput-input': {
                          padding: '8px 14px'
                        }
                      }}
                    >
                      {FIELD_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.is_required}
                        onChange={(e) => handleFieldChange(field.id, 'is_required', e.target.checked)}
                      />
                    }
                    label="Required"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDBox display="flex" justifyContent="flex-end">
                    {isFieldEdited(field.id) && (
                      <MDButton
                        variant="gradient"
                        color="info"
                        size="small"
                        onClick={() => handleUpdateField(field)}
                      >
                        Save Changes
                      </MDButton>
                    )}
                    <MDButton
                      variant="gradient"
                      color="error"
                      size="small"
                      onClick={() => openDeleteDialog(field.table_name, field.id)}
                      sx={{ ml: isFieldEdited(field.id) ? 1 : 0 }}
                    >
                      Delete
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderAddField = () => (
    <Card>
      <MDBox p={3}>
        <MDTypography variant="h6" color="text" mb={2}>
          Add New Custom Field
        </MDTypography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Field Label"
              value={newField.ui_label}
              onChange={(e) => setNewField({ ...newField, ui_label: e.target.value })}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '40px'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Data Type</InputLabel>
              <Select
                value={newField.type}
                label="Data Type"
                onChange={(e) => setNewField({ ...newField, type: e.target.value })}
                sx={{
                  height: '40px',
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 14px'
                  }
                }}
              >
                {FIELD_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={newField.is_required}
                  onChange={(e) => setNewField({ ...newField, is_required: e.target.checked })}
                />
              }
              label="Required"
            />
          </Grid>
        </Grid>
        <MDBox mt={3} display="flex" flexDirection="column" alignItems="center">
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleAddField}
            disabled={!newField.ui_label || customFields.length >= 10 || isAddingField}
          >
            {isAddingField ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Add Custom Field'
            )}
          </MDButton>
          <MDTypography variant="caption" color="text" mt={1}>
            {customFields.length} of 10 custom fields used
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );

  return (
    <MDBox>
      <Tabs
        value={selectedCrmObject?.value || selectedObject}
        onChange={(e, newValue) => setSelectedObject(newValue)}
        variant="scrollable"
        scrollButtons="auto"
      >
        {visibleCrmObjects.map((obj) => (
          <Tab key={obj.value} value={obj.value} label={obj.label} />
        ))}
      </Tabs>

      {visibleCrmObjects.map((obj) => (
        <TabPanel key={obj.value} value={selectedCrmObject?.value || selectedObject} index={obj.value}>
          {loading ? (
            <MDBox textAlign="center">Loading...</MDBox>
          ) : (
            <>
              <MDBox mb={3}>
                <MDTypography variant="h6" color="text">
                  Custom Fields for {obj.label}
                </MDTypography>
              </MDBox>

              {/* Existing Fields Section */}
              {customFields.length > 0 && renderFields()}

              {/* Spacer */}
              <MDBox mt={3} />

              {/* Add Field Section - Always show at bottom */}
              {renderAddField()}
            </>
          )}
        </TabPanel>
      ))}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Custom Field
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this custom field? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={closeDeleteDialog} color="secondary">
            Cancel
          </MDButton>
          <MDButton
            onClick={() => handleDeleteField(deleteDialog.fieldId)}
            color="error"
            variant="gradient"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Delete'
            )}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MDBox>
  );
}

export default CustomFieldsSettings;